import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import {
  EscalonVista,
  MonedaMonto,
  TipoAhorro,
} from '../../../dashboard/components/home-rates/models/home-rates.model';
import * as R from '../../../dashboard/components/home-rates/rules/home-rates.rules';
import { divisorParaTramo } from '../../utils/monto-usdt-tramo';

/**
 * Escalera de tramos por monto: la barra con «Estás aquí», «Tramo k de N», el
 * ahorro frente a la base, lo que falta para la próxima tasa y la lista
 * plegable de todos los tramos.
 *
 * Es de PRESENTACIÓN: no pide nada al back ni guarda nada. El monto lo pone
 * quien lo usa (el simulador del home y las 4 calculadoras) y nunca viaja
 * desde acá a un envío: «Usar X» solo lo escribe en el campo de la pantalla.
 *
 * Toda la cuenta vive en funciones puras de `home-rates.rules.ts`
 * (`tramoDelMonto`, `faltaParaSiguiente`, `ahorroFrenteABase`), con el mismo
 * corte que el back: `desde <= monto`, el primero por defecto.
 */
@Component({
  selector: 'app-escalera-tramos',
  templateUrl: './escalera-tramos.component.html',
  styleUrls: ['./escalera-tramos.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EscaleraTramosComponent {
  /** Los tramos, ordenados por `desde`. Vacía o `null` = no se dibuja nada. */
  @Input() escalera: EscalonVista[] | null = null;

  /** Monto que escribió el cliente, en `moneda`. 0 / null = sin monto. */
  @Input() monto: number | null = 0;

  /** Moneda del monto: `CLP`, `VES` o `USDT`. */
  @Input() moneda: MonedaMonto = 'CLP';

  /** Moneda en la que se cuenta «recibes $Y más» (sólo `RECIBE_MAS`). */
  @Input() monedaDestino: MonedaMonto = 'CLP';

  @Input() tipoAhorro: TipoAhorro = 'RECIBE_MAS';

  /** digitsInfo de la tasa (`'1.2-2'`, `'1.4-4'`). Se formatea en es-CL. */
  @Input() decimales = '1.2-2';

  /**
   * Base para convertir el monto a la unidad del corte cuando difieren
   * (CLP→USDT: `montoClp ÷ base`, como el back). Sin ella, la del primer tramo.
   */
  @Input() base: number | null = null;

  /**
   * `precioCorte` del GET de tramos (23/10): con él el servidor pasa el monto
   * a USDT para elegir el tramo (VES ÷ PROM P2P, CLP ÷ spot). Si es válido
   * (> 0, finito) reemplaza a `base` SOLO en esa división (tramo, «te
   * faltan», «Usar X»); los rótulos y las tasas no cambian. Sin él, como hoy.
   */
  @Input() precioCorte: number | null | undefined = null;

  /** Símbolo de la tasa para «Tu tasa»: `Bs.`, `CLP`. */
  @Input() simbolo = '';

  /** Unidad de la tasa para el pie de la lista: `Bs. por 1 CLP`. */
  @Input() unidad = '';

  /** Sufijo de los ids de accesibilidad (varias escaleras en la misma página). */
  @Input() idBase = 'esc';

  /**
   * La tasa que REALMENTE se cobra, cuando la pantalla la calcula distinto del
   * tramo (CLP→USDT: `min(tramo, segmentación)`; USDT→VES/CLP: la base si el
   * tramo no la mejora). Con ella se pintan «Tu tasa» y el ahorro. Sin ella,
   * la del tramo del monto, como en el home.
   */
  @Input() tasaAplicada: number | null = null;

  /**
   * Contra qué tasa se cuenta el ahorro («frente a la tasa base»). Sin ella,
   * la del primer tramo.
   */
  @Input() tasaReferencia: number | null = null;

  /**
   * Muestra el botón «Usar X» en el «te faltan». Solo lo prenden las
   * calculadoras: el monto se escribe en SU campo y el cliente igual tiene
   * que confirmar. El home no lo usa (su monto nunca viaja).
   */
  @Input() permitirUsar = false;

  /** Tope para «Usar X» (el saldo): por encima no se ofrece. Null = sin tope. */
  @Input() montoMaximo: number | null = null;

  /**
   * Versión para las calculadoras: un solo aviso arriba (lo que falta o, si
   * ya no falta, el ahorro) y el resto dentro de «Ver los N tramos», para no
   * empujar el botón de operar. El home usa la completa.
   */
  @Input() compacta = false;

  /**
   * Formato de los números (tasas, montos, rótulos). Por defecto es-CL, el del
   * home. Cada calculadora pasa el de su campo de monto (`'en-US'`, el de
   * `appMonto`) para que la pantalla no mezcle «2,000» con «2.501».
   */
  @Input() locale: R.LocaleMontos = 'es-CL';

  /**
   * Dónde va esta instancia en el celular (bajo 768 px) cuando la calculadora
   * la parte en dos alrededor del botón de operar:
   *  - `'arriba'`: desde 768 px, la escalera completa (como siempre); bajo 768,
   *    solo un teaser de una línea («Tramo k de N · Te faltan $X · Usar» o «Ya
   *    tienes la mejor tasa»), para no empujar el botón.
   *  - `'abajo'`: bajo 768 px, la escalera completa (va debajo del botón);
   *    desde 768, nada.
   *  - `null` (el home): la escalera completa en todos los anchos.
   */
  @Input() posicion: 'arriba' | 'abajo' | null = null;

  /** El monto que alcanza la próxima tasa, ya en el paso del campo (pesos; centavos en Bs. y USDT). */
  @Output() usarMonto = new EventEmitter<number>();

  // ───────────────────────── Derivados ─────────────────────────

  get n(): number {
    return this.escalera?.length || 0;
  }

  get montoN(): number {
    const m = Number(this.monto);
    return Number.isFinite(m) && m > 0 ? m : 0;
  }

  get baseConv(): number {
    return this.base && this.base > 0 ? this.base : this.escalera?.[0]?.tasa || 0;
  }

  /** Divisor con que se elige el tramo: `precioCorte` si es válido; si no, `base` tal cual (hoy). */
  get divisorCorte(): number | null {
    return divisorParaTramo(this.precioCorte, this.base);
  }

  /** Índice del tramo del monto. */
  get indice(): number {
    return R.tramoDelMonto(this.escalera, this.montoN, this.divisorCorte, this.moneda);
  }

  get actual(): EscalonVista | null {
    const i = this.indice;
    return i >= 0 && this.escalera ? this.escalera[i] : null;
  }

  /** La tasa de «Tu tasa»: la que se cobra si la pantalla la pasa; si no, la del tramo. */
  get tasaTu(): number {
    const t = Number(this.tasaAplicada);
    if (Number.isFinite(t) && t > 0) return t;
    return this.actual?.tasa || 0;
  }

  /** Base del ahorro: la de la pantalla si la pasa; si no, la del primer tramo. */
  get tasaRef(): number {
    const r = Number(this.tasaReferencia);
    if (Number.isFinite(r) && r > 0) return r;
    return this.escalera?.[0]?.tasa || 0;
  }

  get ahorro(): R.Ahorro | null {
    const a = this.actual;
    if (!a || !this.escalera) return null;
    const g = R.ahorroFrenteABase(
      this.montoN,
      this.tasaTu,
      this.tasaRef,
      this.tipoAhorro,
      this.moneda,
      this.monedaDestino,
    );
    return g.valor > 0.004 ? g : null;
  }

  get falta(): R.FaltaSiguiente | null {
    const f = R.faltaParaSiguiente(this.escalera, this.montoN, this.divisorCorte, this.moneda);
    // Con la tasa cobrada a la vista (segmentación), el tramo siguiente tiene
    // que mejorarla: si no, «la próxima tasa» sería peor que la que ya tiene.
    if (f && Number(this.tasaAplicada) > 0 && !this.mejora(f.siguiente.tasa, this.tasaTu)) return null;
    return f;
  }

  /** `a` le conviene más al cliente que `b` (con media centésima de tolerancia). */
  private mejora(a: number, b: number): boolean {
    return this.tipoAhorro === 'PAGA_MENOS' ? b - a > 0.005 : a - b > 0.005;
  }

  /**
   * Monto para «Usar X»: el menor que cae en la próxima tasa según el
   * servidor, en el paso del campo (pesos enteros; Bs. y USDT al centavo; ver
   * `montoParaCorte`). Es el mismo con que se cuenta «Te faltan». Null si no
   * se ofrece.
   */
  get montoUsar(): number | null {
    const f = this.falta;
    if (!this.permitirUsar || !f) return null;
    const v = f.montoSiguiente;
    if (!(v > 0)) return null;
    const max = Number(this.montoMaximo);
    if (this.montoMaximo != null && Number.isFinite(max) && v > max) return null;
    return v;
  }

  usar(): void {
    const v = this.montoUsar;
    if (v != null) this.usarMonto.emit(v);
  }

  /** Lo que ganaría con el monto que alcanza el tramo siguiente. */
  get ahorroSiguiente(): R.Ahorro | null {
    const f = this.falta;
    if (!f || !this.escalera) return null;
    return R.ahorroFrenteABase(
      f.montoSiguiente,
      f.siguiente.tasa,
      this.tasaRef,
      this.tipoAhorro,
      this.moneda,
      this.monedaDestino,
    );
  }

  get enLaMejor(): boolean {
    return this.n > 0 && this.indice === this.n - 1;
  }

  get aproximado(): boolean {
    return R.montoAproximado(this.escalera, this.moneda);
  }

  /** Posición de la marca «Estás aquí»: pegada a los bordes en el primer y el último tramo. */
  get marca(): { [k: string]: string } {
    const n = this.n || 1;
    const i = Math.max(0, this.indice);
    const medio = 100 / n / 2;
    if (i === 0) return { left: '0', '--px': `${medio}%` };
    if (i === n - 1) return { right: '0', '--px': `calc(100% - ${medio}%)` };
    return { left: `${((i + 0.5) / n) * 100}%`, transform: 'translateX(-50%)', '--px': '50%' };
  }

  get ejeDesde(): string {
    return this.escalera?.length ? R.corteCorto(this.escalera[0], this.moneda, this.baseConv, this.locale) : '';
  }

  get ejeHasta(): string {
    const e = this.escalera;
    return e?.length ? `${R.corteCorto(e[e.length - 1], this.moneda, this.baseConv, this.locale)}+` : '';
  }

  /** Id del bloque «Tu tasa»: la instancia de abajo lleva sufijo para no repetir ids. */
  get idTu(): string {
    return this.idBase + (this.posicion === 'abajo' ? '-m' : '') + '-tu';
  }

  // ───────────────────────── Formato (`locale`) ─────────────────────────

  tasa(v: number): string {
    return R.formatearTasa(v, this.decimales, this.locale);
  }

  dinero(v: number, moneda: MonedaMonto | string): string {
    return R.formatearDinero(v, moneda, this.locale);
  }

  /** El monto exacto que escribe «Usar X» (con decimales si los tiene). */
  montoExacto(v: number): string {
    return R.formatearMonto(v, this.moneda, this.locale);
  }

  /**
   * Rótulo de un tramo de la lista. En es-CL, el que ya trae la escalera; en
   * otro formato se rearma con el mismo corte y la misma base.
   */
  rotulo(e: EscalonVista): string {
    if (this.locale === 'es-CL') return e.rotulo;
    return R.rotuloCorte(e.desde, e.unidadCorte, this.moneda, this.baseConv, this.locale);
  }

  /** Prefijo «≈ » cuando el corte se convierte de otra moneda. */
  get aprox(): string {
    return this.aproximado ? '≈ ' : '';
  }

  trackEscalon(i: number, e: EscalonVista): string {
    return `${i}:${e.desde}`;
  }
}
